import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import ArticleDialog, { EMPTY_ARTICLE } from "@/components/ArticleDialog";
import { fmtDate } from "@/components/ArticleBody";

export default function ArticlesPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/articles").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const remove = async (a) => {
    if (!window.confirm(`Supprimer définitivement l'article « ${a.title} » ?`)) return;
    try { await api.delete(`/admin/articles/${a.article_id}`); toast.success("Article supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const published = items.filter((a) => a.status === "published").length;

  return (
    <div className="space-y-4" data-testid="articles-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#475569]" data-testid="articles-count">{items.length} article(s) · {published} publié(s)</p>
        <div className="flex flex-wrap gap-2">
          <a href="/conseils#articles" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm px-4 py-2 border border-gray-300 rounded-full hover:border-[#1B2A4A] transition-colors"><ExternalLink className="w-4 h-4" /> Voir le blog</a>
          <Button data-testid="article-add" onClick={() => setEditing({ ...EMPTY_ARTICLE })} className="rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold"><Plus className="w-4 h-4 mr-1" /> Nouvel article</Button>
        </div>
      </div>
      <div className="bg-white border border-gray-200 divide-y divide-gray-100">
        {items.length === 0 && <p className="text-gray-400 py-10 text-center text-sm">Aucun article.</p>}
        {items.map((a) => (
          <div key={a.article_id} data-testid={`article-row-${a.article_id}`} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4">
            <div className="w-full sm:w-28 aspect-[16/10] bg-[#F8F6F0] overflow-hidden shrink-0">{a.image && <img src={a.image} alt="" className="w-full h-full object-cover" />}</div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#1B2A4A]">{a.title}</div>
              <div className="text-xs text-[#475569] mt-1 flex flex-wrap gap-x-3">
                <span className={a.status === "published" ? "text-green-700" : "text-amber-700"} data-testid={`article-status-${a.article_id}`}>{a.status === "published" ? "Publié" : "Brouillon"}</span>
                {a.published_at && <span>{fmtDate(a.published_at)}</span>}
                {a.sector && <span>{a.sector}</span>}
                <span className="text-[#94A3B8]">/conseils/{a.slug}</span>
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              {a.status === "published" && <a href={`/conseils/${a.slug}`} target="_blank" rel="noreferrer" data-testid={`article-open-${a.article_id}`} className="p-2 hover:text-[#C9A85A]"><ExternalLink className="w-4 h-4" /></a>}
              <Button size="sm" variant="ghost" onClick={() => setEditing(a)} data-testid={`article-edit-${a.article_id}`} className="rounded-none"><Pencil className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => remove(a)} data-testid={`article-delete-${a.article_id}`} className="rounded-none text-red-600"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      <ArticleDialog article={editing} onOpenChange={(v) => !v && setEditing(null)} onSaved={load} />
    </div>
  );
}
